import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type Mailbox, type Me } from "../../lib/api-client";
import type { Theme } from "../../lib/theme";
import { useIsMobile } from "../../lib/useIsMobile";
import ComposePane, { type ComposeDraft } from "./ComposePane";
import MailboxNav from "./MailboxNav";
import MessageList from "./MessageList";
import MessageView from "./MessageView";
import TopBar from "./TopBar";

interface MailLayoutProps {
  me: Me;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
}

const LIST_WIDTH_KEY = "berrymail-list-width";
const LIST_WIDTH_MIN = 240;
const LIST_WIDTH_MAX = 520;
const LIST_WIDTH_DEFAULT = 280;

export default function MailLayout({ me, theme, onThemeChange }: MailLayoutProps) {
  const { mailboxId, messageId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  // Solo relevante en mobile: el nav de carpetas vive en un drawer que se
  // abre con el botón hamburguesa de TopBar, en vez de ser una columna fija
  // siempre visible como en desktop.
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [mailboxes, setMailboxes] = useState<Mailbox[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // null = no está componiendo. {} = "Redactar" desde cero. Con campos =
  // Responder/Responder a todos/Reenviar (ver MessageView.tsx).
  const [composeDraft, setComposeDraft] = useState<ComposeDraft | null>(null);
  const [listWidth, setListWidth] = useState<number>(() => {
    const saved = Number(localStorage.getItem(LIST_WIDTH_KEY));
    return saved >= LIST_WIDTH_MIN && saved <= LIST_WIDTH_MAX ? saved : LIST_WIDTH_DEFAULT;
  });
  const listWidthRef = useRef(listWidth);
  listWidthRef.current = listWidth;

  // refreshToken: se incrementa para forzar a MessageList a repedir la lista
  // completa (archivar/eliminar mueven el correo fuera del mailbox actual,
  // no alcanza con un patch local). justSeenMessageId: en cambio, marcar
  // leído sí se puede reflejar con un patch local barato, sin round-trip.
  const [refreshToken, setRefreshToken] = useState(0);
  const [justSeenMessageId, setJustSeenMessageId] = useState<string | null>(null);

  useEffect(() => {
    api
      .mailboxes()
      .then(setMailboxes)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  function refreshMailboxes() {
    api
      .mailboxes()
      .then(setMailboxes)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  // Fix del bug reportado: abrir un correo no bajaba el contador de no
  // leídos del sidebar. Ajuste optimista acá (evita un round-trip extra) +
  // aviso a MessageList para que el ítem deje de mostrarse en negrita.
  function handleMessageSeen(seenMailboxId: string, seenMessageId: string) {
    setMailboxes(
      (prev) =>
        prev?.map((mb) =>
          mb.id === seenMailboxId && mb.unreadEmails
            ? { ...mb, unreadEmails: mb.unreadEmails - 1 }
            : mb,
        ) ?? prev,
    );
    setJustSeenMessageId(seenMessageId);
  }

  // Archivar/eliminar sacan el correo del mailbox actual — refrescamos
  // conteos y lista, y volvemos a la vista sin mensaje seleccionado.
  function handleMessageRemoved(fromMailboxId: string) {
    refreshMailboxes();
    setRefreshToken((t) => t + 1);
    navigate(`/mailbox/${fromMailboxId}`);
  }

  function handleMailboxCreated(mailbox: Mailbox) {
    setMailboxes((prev) => (prev ? [...prev, mailbox] : [mailbox]));
  }

  function handleMailboxDeleted(deletedId: string) {
    setMailboxes((prev) => prev?.filter((mb) => mb.id !== deletedId) ?? prev);
    // Si estabas viendo esa carpeta, no te puede quedar seleccionada una
    // que ya no existe — el efecto de abajo manda al inbox apenas
    // mailboxId quede undefined.
    if (mailboxId === deletedId) {
      navigate("/mailbox", { replace: true });
    }
  }

  // Sin mailbox seleccionado todavía: mandamos al inbox (o al primero que
  // haya) apenas sepamos qué mailboxes existen.
  useEffect(() => {
    if (!mailboxId && mailboxes && mailboxes.length > 0) {
      const inbox = mailboxes.find((m) => m.role === "inbox") ?? mailboxes[0];
      navigate(`/mailbox/${inbox.id}`, { replace: true });
    }
  }, [mailboxId, mailboxes, navigate]);

  function handleResizeStart(e: React.MouseEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = listWidthRef.current;

    function onMouseMove(moveEvent: MouseEvent) {
      const next = Math.min(
        LIST_WIDTH_MAX,
        Math.max(LIST_WIDTH_MIN, startWidth + (moveEvent.clientX - startX)),
      );
      setListWidth(next);
    }
    function onMouseUp() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      localStorage.setItem(LIST_WIDTH_KEY, String(listWidthRef.current));
    }
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  const navProps = {
    mailboxes,
    selectedMailboxId: mailboxId,
    error,
    onCompose: () => setComposeDraft({}),
    onMailboxCreated: handleMailboxCreated,
    onMailboxDeleted: handleMailboxDeleted,
  };

  const composePane = composeDraft && (
    <ComposePane
      draft={composeDraft}
      onClose={() => setComposeDraft(null)}
      onSent={() => {
        setComposeDraft(null);
        // Mostrar el correo recién enviado en Sent en vez de dejar
        // al usuario adivinando si de verdad salió.
        const sent = mailboxes?.find((m) => m.role === "sent");
        if (sent) navigate(`/mailbox/${sent.id}`);
      }}
    />
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "100vh",
        fontFamily: "var(--font-body)",
      }}
    >
      <TopBar userEmail={me.email} onMenuClick={isMobile ? () => setIsNavOpen(true) : undefined} />

      {isMobile ? (
        <>
          {/* Mobile: sin columnas lado a lado — el nav vive en un drawer
              propio (abajo) y acá solo se muestra un panel a pantalla
              completa por vez: lista, lectura o composición. */}
          <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
            {mailboxId &&
              (composePane ?? (messageId ? (
                <MessageView
                  mailboxId={mailboxId}
                  messageId={messageId}
                  theme={theme}
                  isTrash={mailboxes?.find((m) => m.id === mailboxId)?.role === "trash"}
                  mailboxes={mailboxes}
                  myEmail={me.email}
                  onSeen={handleMessageSeen}
                  onRemoved={handleMessageRemoved}
                  onCompose={setComposeDraft}
                  onBack={() => navigate(`/mailbox/${mailboxId}`)}
                />
              ) : (
                <MessageList
                  mailboxId={mailboxId}
                  selectedMessageId={messageId}
                  refreshToken={refreshToken}
                  justSeenMessageId={justSeenMessageId}
                />
              )))}
          </div>

          {isNavOpen && (
            <div
              onClick={() => setIsNavOpen(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 10 }}
            />
          )}
          <div
            style={{
              position: "fixed",
              top: 0,
              bottom: 0,
              left: 0,
              width: 260,
              maxWidth: "80vw",
              zIndex: 11,
              boxShadow: isNavOpen ? "2px 0 16px rgba(0,0,0,0.25)" : "none",
              transform: isNavOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 0.2s ease",
            }}
          >
            <MailboxNav {...navProps} onNavigate={() => setIsNavOpen(false)} />
          </div>
        </>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `220px ${listWidth}px minmax(0, 1fr)`,
            // minHeight:0 en el contenedor + en cada hijo del grid: sin esto,
            // los items de un grid no se achican por debajo de su contenido, y
            // en vez de scrollear cada columna, termina scrolleando la página
            // entera. Con esto cada columna scrollea la suya.
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <MailboxNav {...navProps} />
          {mailboxId ? (
            <>
              <div style={{ position: "relative", minHeight: 0, display: "flex" }}>
                <MessageList
                  mailboxId={mailboxId}
                  selectedMessageId={messageId}
                  refreshToken={refreshToken}
                  justSeenMessageId={justSeenMessageId}
                />
                {/* Handle de resize — arrastrar para achicar/agrandar la lista.
                    El ancho se guarda en localStorage y se recuerda entre sesiones. */}
                <div
                  onMouseDown={handleResizeStart}
                  title="Arrastrar para redimensionar"
                  style={{
                    position: "absolute",
                    top: 0,
                    right: -3,
                    width: 6,
                    height: "100%",
                    cursor: "col-resize",
                    zIndex: 1,
                  }}
                />
              </div>
              {composePane ?? (
                <MessageView
                  mailboxId={mailboxId}
                  messageId={messageId}
                  theme={theme}
                  isTrash={mailboxes?.find((m) => m.id === mailboxId)?.role === "trash"}
                  mailboxes={mailboxes}
                  myEmail={me.email}
                  onSeen={handleMessageSeen}
                  onRemoved={handleMessageRemoved}
                  onCompose={setComposeDraft}
                />
              )}
            </>
          ) : (
            <div style={{ gridColumn: "span 2" }} />
          )}
        </div>
      )}
    </div>
  );
}
